import { type ContentTypesFlagProps } from '@canvas/frame-api-types';

// Shared window object interface
interface SharedFlagData {
  flags: Array<{ flagId: string } & ContentTypesFlagProps>;
  listeners: Array<(flags: Array<{ flagId: string } & ContentTypesFlagProps>) => void>;
}

// Global type declaration for the shared object
declare global {
  interface Window {
    canvasDevFlags?: SharedFlagData;
  }
}

// Initialize or get the shared flag data object
function getOrCreateSharedFlagData(): SharedFlagData {
  // Always operate on the top window to ensure flags are shared between iframe
  // and parent
  // eslint-disable-next-line no-restricted-syntax
  const topWindow = window.top as Window;

  if (!topWindow.canvasDevFlags) {
    topWindow.canvasDevFlags = {
      flags: [],
      listeners: [],
    };
  }
  return topWindow.canvasDevFlags;
}

// Notify all listeners of flag changes
function notifyListeners(flags: Array<{ flagId: string } & ContentTypesFlagProps>) {
  const sharedData = getOrCreateSharedFlagData();
  sharedData.listeners.forEach((listener) => {
    try {
      listener(flags);
    } catch (error) {
      console.error('Error in flag listener:', error);
    }
  });
}

export const addFlag = (flagId: string, flagProps: ContentTypesFlagProps) => {
  const sharedData = getOrCreateSharedFlagData();
  sharedData.flags.push({ flagId, ...flagProps });
  notifyListeners(sharedData.flags);
};

export const removeFlag = (flagId: string) => {
  const sharedData = getOrCreateSharedFlagData();
  const index = sharedData.flags.findIndex((flag) => flag.flagId === flagId);
  if (index !== -1) {
    sharedData.flags.splice(index, 1);
    notifyListeners(sharedData.flags);
  }
};

export const getCurrentFlags = () => {
  const sharedData = getOrCreateSharedFlagData();
  return sharedData.flags;
};

// Subscribe to flag changes
export const subscribeToFlags = (
  listener: (flags: Array<{ flagId: string } & ContentTypesFlagProps>) => void,
) => {
  const sharedData = getOrCreateSharedFlagData();
  sharedData.listeners.push(listener);

  // Return unsubscribe function
  return () => {
    const index = sharedData.listeners.indexOf(listener);
    if (index !== -1) {
      sharedData.listeners.splice(index, 1);
    }
  };
};

// Unsubscribe from flag changes
export const unsubscribeFromFlags = (
  listener: (flags: Array<{ flagId: string } & ContentTypesFlagProps>) => void,
) => {
  const sharedData = getOrCreateSharedFlagData();
  const index = sharedData.listeners.indexOf(listener);
  if (index !== -1) {
    sharedData.listeners.splice(index, 1);
  }
};
